import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useState } from 'react';
import { Dimensions, Pressable, View } from 'react-native';
import Svg, { Circle, Defs, FeGaussianBlur, Filter } from 'react-native-svg';
import styled from 'styled-components/native';
import CheckIcon from './CheckIcon';
import XIcon from './XIcon';

// --- Styled Components ---

const ProductFitWrapper = styled.View`
  width: 100%;
  align-items: center;
  paddingTop: 0px;
  overflow: hidden;
`;

const Container = styled.View`
  max-width: 1264px;
  width: 100%;
  align-items: center;
  overflow: hidden;
`;

const CardFrame = styled.View`
  width: 100%;
  border-radius: 16px;
  align-items: center;
  justify-content: flex-start;
  padding-top: 80px;
  position: relative;
`;

const GradientBackground = styled(LinearGradient).attrs({
  colors: ['rgba(10, 19, 33, 1)', 'rgba(8, 8, 8, 1)'],
  locations: [0.21, 1],
})`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px;
`;

const BlurredCircle = () => (
  <Svg height="609" width="609" style={{ position: 'absolute' }}>
    <Defs>
      <Filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
        <FeGaussianBlur in="SourceGraphic" stdDeviation="54" />
      </Filter>
    </Defs>
    <Circle cx="304.5" cy="304.5" r="250" fill="#053FED" opacity="0.2" filter="url(#blur)" />
  </Svg>
);

const TitleWrapper = styled.View`
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
  z-index: 10;
`;

const TitleText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-weight: 500;
  font-size: ${(props) => (props.$isMobile ? '24px' : '32px')};
  line-height: ${(props) => (props.$isMobile ? '38.4px' : '51.2px')};
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 40px;
`;

const TabSelectorContainer = styled.View`
  display: flex;
  flex-direction: row;
  height: 53px;
  padding: 0 12px;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border-radius: 99px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const Tab = styled(Pressable)`
  display: flex;
  width: 140px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  border-radius: 99px;
  background-color: ${({ active }) => (active ? '#FFFFFF' : 'transparent')};
`;

const TabText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-weight: 500;
  font-size: 14px;
  line-height: 22.4px;
  color: ${({ active }) => (active ? '#0E0E0E' : '#FFFFFF')};
`;

const CardsGrid = styled.View`
  width: ${(props) => (props.$isMobile ? '100%' : '816px')};
  max-width: ${(props) => (props.$isMobile ? '100%' : '816px')};
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  z-index: 10;
  padding-bottom: 60px;
  padding-horizontal: ${(props) => (props.$isMobile ? '16px' : '0px')};
`;

const FitCard = styled.View`
  width: 100%;
  padding-top: 24px;
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  minHeight: 140px;
`;

const gradientProps = (() => {
  const rad = (153 * Math.PI) / 180;
  const startX = 0.5 - Math.cos(rad) * 0.5;
  const startY = 0.5 + Math.sin(rad) * 0.5;
  const endX = 0.5 + Math.cos(rad) * 0.5;
  const endY = 0.5 - Math.sin(rad) * 0.5;
  return {
    colors: ['rgba(255, 255, 255, 0.08)', 'rgba(255, 255, 255, 0.16)'],
    start: { x: startX, y: startY },
    end: { x: endX, y: endY },
  };
})();

const FitItemText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-weight: 500;
  font-size: 16px;
  line-height: 25.6px;
  color: #FFFFFF;
  text-align: center;
  font-style: normal;
`;

const IconWrapper = styled.View`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const FitItem = ({ text, isGood }) => (
  <LinearGradient
    {...gradientProps}
    style={{ borderRadius: 8, padding: 1, flexBasis: 400, flexGrow: 0, flexShrink: 1 }}
  >
    <FitCard>
      <IconWrapper>{isGood ? <CheckIcon /> : <XIcon />}</IconWrapper>
      <FitItemText>{text}</FitItemText>
    </FitCard>
  </LinearGradient>
);

// --- The Main Component ---

const ProductFit = ({ cardsData = [], scrollY, containerPosition }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 for great, 1 for poor

  // Track screen width for responsive layout
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const BREAKPOINT_WIDTH = 768;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const isMobile = screenWidth < BREAKPOINT_WIDTH;

  const handleTabPress = (index) => {
    setActiveTab(index);
  };

  const currentCardData = cardsData[activeTab] || { type: 'good', title: 'great', items: [] };

  return (
    <ProductFitWrapper>
      <Container>
        <CardFrame>
          <GradientBackground />
          {!isMobile && (
            <>
              <View style={{ position: 'absolute', left: -460, top: -31 }}>
                <BlurredCircle />
              </View>
              <View style={{ position: 'absolute', right: 160, top: -31 }}>
                <BlurredCircle />
              </View>
            </>
          )}

          <TitleWrapper>
            <TitleText $isMobile={isMobile}>Personalised just for you</TitleText>
            <TabSelectorContainer>
              <Tab active={activeTab === 0} onPress={() => handleTabPress(0)}>
                <TabText active={activeTab === 0}>Great fit</TabText>
              </Tab>
              <Tab active={activeTab === 1} onPress={() => handleTabPress(1)}>
                <TabText active={activeTab === 1}>Poor fit</TabText>
              </Tab>
            </TabSelectorContainer>
          </TitleWrapper>

          <CardsGrid $isMobile={isMobile}>
            {currentCardData.items.map((item, idx) => (
              <FitItem key={idx} text={item} isGood={currentCardData.type === 'good'} />
            ))}
          </CardsGrid>
        </CardFrame>
      </Container>
    </ProductFitWrapper>
  );
};

export default ProductFit;
